import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/api';

export interface BackendStatus {
  isOnline: boolean;
  version: string | null;
  buildTime: string | null;
  environment: string | null;
  uptimeSeconds: number | null;
  lastChecked: Date | null;
}

const INITIAL_STATUS: BackendStatus = {
  isOnline: false,
  version: null,
  buildTime: null,
  environment: null,
  uptimeSeconds: null,
  lastChecked: null,
};

const CHECK_INTERVAL_MS = 60_000; // 60 Sekunden
const REQUEST_TIMEOUT_MS = 5_000; // 5 Sekunden

/**
 * Periodischer Health-Check gegen GET /api/version.
 * Liefert den Online-Status und Metadaten des Backends.
 */
export function useBackendStatus(): BackendStatus {
  const [status, setStatus] = useState<BackendStatus>(INITIAL_STATUS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkBackend = useCallback(async () => {
    try {
      const response = await apiClient.get('/version', {
        timeout: REQUEST_TIMEOUT_MS,
      });

      const data = response.data;

      setStatus({
        isOnline: true,
        version: data.version ?? null,
        buildTime: data.build_time ?? null,
        environment: data.environment ?? null,
        uptimeSeconds: data.uptime_seconds ?? null,
        lastChecked: new Date(),
      });
    } catch {
      setStatus((prev) => ({
        ...prev,
        isOnline: false,
        version: null,
        buildTime: null,
        environment: null,
        uptimeSeconds: null,
        lastChecked: new Date(),
      }));
    }
  }, []);

  useEffect(() => {
    // Initialer Check sofort
    checkBackend();

    // Wiederholung alle 60 Sekunden
    intervalRef.current = setInterval(checkBackend, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkBackend]);

  return status;
}
